import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationError } from '@angular/router';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { AppPathsMap } from '@core/routing/app-paths';
import { ErrorSeverityMap, StorageService } from '@kerberos-compliance/compliance-shared-ui';
import { filter } from 'rxjs/operators';
import { AmlDeskErrorHandlerService } from './aml-desk-error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class ChunkLoadingErrorHandlerService {
  private readonly navigationService = inject(NavigationService);

  private readonly errorHandlerService = inject(AmlDeskErrorHandlerService);

  private readonly storageService = inject(StorageService);

  private readonly destroyRef = inject(DestroyRef);

  private static readonly ReloadStorageKey = 'aml_desk_chunk_error_reload';

  private static readonly ReloadCooldownMilliseconds = 10_000;

  private static readonly ChunkLoadingErrorMarkers = [
    'ChunkLoadError',
    'Loading chunk',
    'Failed to fetch dynamically imported module',
    'Error loading chunk',
    'Loading CSS chunk',
  ] as const;

  /**
   * Initializes the service by setting up a subscription to router events that detects navigation errors.
   * It filters events to capture instances of NavigationError and invokes the handleNavigationError method accordingly.
   */
  public initialize(): void {
    this.navigationService
      .getEvents()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationError),
      )
      .subscribe((event) => {
        this.handleNavigationError(event);
      });
  }

  /**
   * Handles navigation errors by checking if the error is related to chunk loading.
   * If the error is a chunk loading error, it logs the error and attempts automatic recovery
   * by purging client state (app storage) and reloading the page. If a reload was already
   * attempted within the cooldown window, redirects to the login error page instead
   * of silently failing, so the user always has a path forward.
   *
   * @param {NavigationError} error - The navigation error object containing details about the error.
   */
  private handleNavigationError(error: NavigationError): void {
    if (!this.isChunkLoadingError(error)) {
      return;
    }

    this.errorHandlerService.handleError({
      error: error.error,
      metadata: {
        severity: ErrorSeverityMap.High,
        component: 'ChunkLoadingErrorHandlerService',
        action: 'Failed to load application module',
        message: 'errorMessages.chunkLoadError',
      },
    });

    this.attemptRecovery(error.error);
  }

  /**
   * Recovers from a chunk loading error by purging corrupt client state and reloading the app.
   * On the first attempt within the cooldown window, performs a full reload after clearing storage.
   * If a chunk error recurs inside the cooldown, hard-navigates to the login error page so the
   * user is never stranded on an infinite loading spinner.
   *
   * @param {unknown} originalError - The original error that triggered the recovery attempt.
   * @returns {void} This method does not return a value.
   */
  private attemptRecovery(originalError: unknown): void {
    const hasReloadedRecently = this.hasReloadedRecently();

    this.purgeClientState();

    if (hasReloadedRecently) {
      this.errorHandlerService.handleError({
        error: originalError,
        metadata: {
          severity: ErrorSeverityMap.High,
          component: 'ChunkLoadingErrorHandlerService',
          action: 'Chunk loading failed after automatic reload attempt',
          message: 'errorMessages.chunkLoadErrorPersistent',
        },
      });

      sessionStorage.removeItem(ChunkLoadingErrorHandlerService.ReloadStorageKey);
      globalThis.location.assign(`/${AppPathsMap.Error.LoginError}`);
      return;
    }

    sessionStorage.setItem(ChunkLoadingErrorHandlerService.ReloadStorageKey, Date.now().toString());
    globalThis.location.reload();
  }

  /**
   * Checks whether a recovery reload has already happened inside the cooldown window.
   * Uses sessionStorage so the marker is scoped to the current tab and cleared when it closes.
   *
   * @returns {boolean} True if a reload marker was set within the cooldown window.
   */
  private hasReloadedRecently(): boolean {
    const lastReloadTimestamp = sessionStorage.getItem(ChunkLoadingErrorHandlerService.ReloadStorageKey);
    if (lastReloadTimestamp === null) {
      return false;
    }

    // eslint-disable-next-line unicorn/prefer-number-coercion -- keep parseInt semantics for stored value
    const elapsed = Date.now() - Number.parseInt(lastReloadTimestamp, 10);
    return elapsed <= ChunkLoadingErrorHandlerService.ReloadCooldownMilliseconds;
  }

  /**
   * Wipes client-side state that can hold references to stale chunk URLs or a corrupt session:
   * the app's prefixed storage via StorageService. Failures are swallowed so the caller can
   * still reload — partial cleanup is preferable to no recovery.
   *
   * @returns {void} Returns nothing; failures are intentionally ignored.
   */
  private purgeClientState(): void {
    try {
      this.storageService.clear();
    } catch {
      // Non-critical — still proceed with recovery.
    }
  }

  /**
   * Determines if the given error relates to a chunk loading failure.
   *
   * @param {NavigationError} error - The navigation error object to be evaluated.
   * @returns {boolean} Returns true if the error is identified as a chunk loading error, otherwise false.
   */
  private isChunkLoadingError(error: NavigationError): boolean {
    const errorText = this.getErrorText(error.error);

    return ChunkLoadingErrorHandlerService.ChunkLoadingErrorMarkers.some((marker) => errorText.includes(marker));
  }

  /**
   * Extracts and returns a textual representation of the provided error.
   *
   * @param {unknown} rawError The error input, which can be of any type.
   * @returns {string} A string describing the error, including the stack trace if available,
   * or the error message, or a stringifies version of the input.
   */
  private getErrorText(rawError: unknown): string {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty stack/message must fall through
    return rawError instanceof Error ? rawError.stack || rawError.message || String(rawError) : String(rawError);
  }
}
